// JAWS vs VoiceOver (see doc/jaws-voiceover-accessibility-reference.md §5):
// Step progress is an sr-only h2 on each step (via aria-describedby on the h1).
// On step change, focus moves to the h1 so AT reads the title then progress.

import React, { useLayoutEffect, useRef } from 'react'
import DataProvider, { useData } from './DataProvider'
import StepperDisplay from './StepperDisplay'
import { AnnouncerProvider, focusStepOnChange } from './Announcer'
import useTranslation, {
  TranslationProvider,
} from '../shared/TranslationProvider'
import { ThemeProvider } from '../shared/ThemeProvider'
import translations from './locales.json'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3/Step3'
import Step4 from './Step4'
import Step5 from './Step5'
import { Box } from '@mui/joy'
import SelectOnboarding from '../SelectOnboarding/index'

const OnboardingStepsInternal = ({ select, op_next_gen }) => {
  if (select && op_next_gen) return <SelectOnboarding />
  return (
    <div className="mx-auto max-w-7xl py-6 sm:px-6 lg:px-8">
      <DefaultOnboarding />
    </div>
  )
}

const DefaultOnboarding = () => {
  const { t } = useTranslation()
  const { step } = useData()
  const hasMountedRef = useRef(false)
  const previousStepRef = useRef(step)

  useLayoutEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      previousStepRef.current = step
      return
    }

    if (previousStepRef.current === step) return

    previousStepRef.current = step

    focusStepOnChange()
  }, [step])

  return (
    <div data-testid="a11y-capture--04-onboarding-flow--default">
      {/* Plan 04 onboarding — capture root for visual/a11y baselines; YNS-018–020 / CTS-9205, CTS-9218, CTS-9266 */}
      <div className="px-4 md:p-0">
        <StepperDisplay />
        {step == 1 && <Step1 />}
        {step == 2 && <Step2 />}
        {step == 3 && <Step3 />}
        {step == 4 && <Step4 />}
        {step == 5 && <Step5 />}
      </div>

      <Box className="pt-4 text-center">
        <a href="/" className="text-sm text-black underline hover:font-bold">
          {t('skip_to_dashboard')}
        </a>
      </Box>
    </div>
  )
}

const OnboardingSteps = ({
  locale,
  activeStep = 1,
  statoCustomContent = null,
  time_zones,
  select = false,
  op_next_gen = false,
}) => {
  const scope = 'javascript.components.OnboardingSteps'
  return (
    <TranslationProvider
      translations={translations}
      locale={locale || 'en'}
      scope={scope}
    >
      <DataProvider
        fetchOnLoad
        activeStep={activeStep}
        statoCustomContent={statoCustomContent}
        time_zones={time_zones}
        syncBrowserHistory={!(select && op_next_gen)}
      >
        <ThemeProvider>
          <AnnouncerProvider>
            <OnboardingStepsInternal
              select={select}
              op_next_gen={op_next_gen}
            />
          </AnnouncerProvider>
        </ThemeProvider>
      </DataProvider>
    </TranslationProvider>
  )
}

export default OnboardingSteps
